<template lang="html">
    <report-section
        :id="id"
        title="Climate"
    >
        <p>
            The climate of the {{ watershedName }} watershed has been characterized using gridded climate models<sup>
                <footnote-tooltip
                    :footnote-number="18"
                />
            </sup>. These models have been used to calculate long term average temperature, rain and snow
            accumulations. The timing and amount of precipitation is a strong indicator of water availability, and the
            state it falls in (rain or snow) determines what happens when it reaches the ground - whether it
            accumulates and melts when temperatures warm, or infiltrates and runs off rapidly after reaching the
            ground. Three estimates of future climate for the 2041-2070 time period have been assessed and used to
            illustrate a range of potential future conditions. The shaded areas on each chart show a range of future
            conditions, and the relationship with the solid line (showing the historical average) helps understand at
            what time of the year the temperature, precipitation, and precipitation as snow may be different.
        </p>
        <monthly-climate-chart
            :data="temperatureData"
            y-axis-label="Temperature (°C)"
            unit-of-measurement=" °C"
            :line-color="climateChartColors.temperatureLine"
            :area-color="climateChartColors.temperatureArea"
            class="climate-chart"
        />
        <monthly-climate-chart
            :data="precipitationData"
            y-axis-label="Precipitation (mm)"
            unit-of-measurement=" mm"
            :line-color="climateChartColors.precipitationLine"
            :area-color="climateChartColors.precipitationArea"
            class="climate-chart"
        />
        <monthly-climate-chart
            :data="snowData"
            y-axis-label="Snow (mm)"
            unit-of-measurement=" mm"
            :line-color="climateChartColors.snowLine"
            :area-color="climateChartColors.snowArea"
            class="climate-chart"
        />

        <hr class="subsection-divider is-visible-pdf is-hidden-web">

        <div class="pdf-legend is-hidden">
            <div class="legend-item">
                <div class="legend-item-label">
                    Normal / Historical Average
                </div>
                <div
                    class="color-box"
                    :style="{ backgroundColor: climateChartColors.temperatureLine }"
                />
                <div
                    class="color-box"
                    :style="{ backgroundColor: climateChartColors.precipitationLine }"
                />
                <div
                    class="color-box"
                    :style="{ backgroundColor: climateChartColors.snowLine }"
                />
            </div>
            <div class="legend-item">
                <div class="legend-item-label">
                    Projected Average for the 2050s
                </div>
                <div
                    class="color-box"
                    :style="{ backgroundColor: climateChartColors.temperatureArea }"
                />
                <div
                    class="color-box"
                    :style="{ backgroundColor: climateChartColors.precipitationArea }"
                />
                <div
                    class="color-box"
                    :style="{ backgroundColor: climateChartColors.snowArea }"
                />
            </div>
        </div>
    </report-section>
</template>

<script>
import FootnoteTooltip from '@foundryspatial/footnote-tooltip';
import sectionMixin from '@foundryspatial/report-section/mixin';

import scssVars from '@/assets/scss/_variables.scss';
import MonthlyClimateChart from '@/components/charts/MonthlyClimateChart.vue';

export default {
    name: 'Climate',

    components: {
        FootnoteTooltip,
        MonthlyClimateChart,
    },

    mixins: [
        sectionMixin,
    ],

    data: () => ({
        climateChartColors: {
            temperatureLine: scssVars.colorClimateTemperatureLine,
            temperatureArea: scssVars.colorClimateTemperatureArea,
            precipitationLine: scssVars.colorClimatePrecipitationLine,
            precipitationArea: scssVars.colorClimatePrecipitationArea,
            snowLine: scssVars.colorClimateSnowLine,
            snowArea: scssVars.colorClimateSnowArea,
        },
    }),

    computed: {
        temperatureData() {
            return {
                line: this.reportData.climateChartData.temperature.historical,
                area: this.reportData.climateChartData.temperature.future,
            };
        },

        precipitationData() {
            return {
                line: this.reportData.climateChartData.precipitation.historical,
                area: this.reportData.climateChartData.precipitation.future,
            };
        },

        snowData() {
            return {
                line: this.reportData.climateChartData.snow.historical,
                area: this.reportData.climateChartData.snow.future,
            };
        },

        watershedName() {
            return this.reportData.overview.watershedName;
        },
    },
};
</script>

<style lang="scss">
#climate.report-section {
    .climate-chart {
        margin-top: 2rem;
    }

    hr.subsection-divider {
        margin: 1em 0;
    }
}

.static-report #climate.report-section {
    .climate-chart {
        margin: 1.25rem 0;

        .chart-legend {
            display: none;
        }
    }

    .pdf-legend {
        display: grid !important;
        grid-template-columns: repeat(2, auto);
        column-gap: 130px;

        .legend-item {
            display: grid;
            grid-template-columns: auto repeat(3, 1em);
            column-gap: 4px;
            align-items: center;

            .color-box {
                width: 1em;
                height: 1em;
                border: 1px solid;
                border-radius: 4px;
                box-sizing: border-box;
            }
        }
    }
}
</style>
